import React from "react";
import { Link } from "@inertiajs/react";
import styled from "styled-components";
import {
  brand_orange_deep,
  brand_gradient,
  card_shadow_md,
  narrowScreenMediaQuery,
} from "../../constants/styles";

function statusFor(progress) {
  if (progress >= 100) return { label: "Selesai", tone: "success", cta: "Tonton Ulang" };
  if (progress > 0)    return { label: `${progress}% ditonton`, tone: "muted", cta: "Lanjut" };
  return { label: "Belum Ditonton", tone: "muted", cta: "Mulai" };
}

function formatDuration(secs) {
  if (!secs || secs <= 0) return null;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m === 0) return `${s} dtk`;
  return s ? `${m} mnt ${s} dtk` : `${m} mnt`;
}

function CurriculumLessonList({ groups, courseSlug, curriculumSlug }) {
  if (!groups || !groups.length) return null;
  const useGroups = groups.length > 1 && groups[0].category;

  // Determine the global "next-up" lesson — the first incomplete lesson in
  // creation order across all groups. Order is stable since the
  // CurriculumDetailPresenter walks `@cur.lessons` and Lesson has
  // `default_scope { order(created_at: :asc) }`.
  const flat = groups.flatMap((g) => g.lessons || []);
  const nextUp = flat.find((l) => (l.progress_percent || 0) < 100);
  const nextId = nextUp?.id;

  return (
    <Section aria-labelledby="curriculum-lesson-list">
      <Title id="curriculum-lesson-list">Daftar Materi</Title>
      {useGroups
        ? groups.map((g, gi) => (
            <Group key={gi}>
              <GroupTitle>{g.category || "Lainnya"}</GroupTitle>
              <Stack>
                {g.lessons.map((lesson, idx) => renderRow(lesson, idx, nextId))}
              </Stack>
            </Group>
          ))
        : (
          <Stack>
            {flat.map((lesson, idx) => renderRow(lesson, idx, nextId))}
          </Stack>
        )}
    </Section>
  );
}

function renderRow(lesson, idx, nextId) {
  const progress = lesson.progress_percent || 0;
  const meta = statusFor(progress);
  const duration = formatDuration(lesson.duration_seconds);
  const isNext = lesson.id === nextId;

  return (
    <Row key={lesson.id} $next={isNext}>
      <IndexBubble className="idx-area" $next={isNext}>{idx + 1}</IndexBubble>
      <Body className="body-area">
        <Heading>
          {lesson.name}
          {!lesson.is_free && <PaidPill>Premium</PaidPill>}
          {isNext && <NextBadge>Lanjut Berikutnya</NextBadge>}
        </Heading>
        <MetaRow>
          {duration && <Meta>{duration}</Meta>}
          {lesson.has_module && <Meta>Buku</Meta>}
          {lesson.has_workbook && <Meta>Workbook</Meta>}
        </MetaRow>
        <ProgressRow>
          <ProgressBar>
            <ProgressFill style={{ width: `${progress}%` }} />
          </ProgressBar>
          <Status $tone={meta.tone}>{meta.label}</Status>
        </ProgressRow>
      </Body>
      <Cta className="cta-area">
        <CtaLink href={lesson.detail_url} $emphasis={isNext}>{meta.cta} →</CtaLink>
      </Cta>
    </Row>
  );
}

export default React.memo(CurriculumLessonList);

const Section = styled.section`
  /* Wrapped by Curriculum.jsx ContentSection — no own bg/padding so it
     blends into the 2-col grid. The Title acts as the section header. */
`;

const Title = styled.h2`
  font-size: 26px;
  font-weight: 800;
  color: #1f2937;
  margin: 0 0 22px;
  letter-spacing: -0.005em;
  [data-theme="dark"] & { color: #f3f4f6; }
`;

const Group = styled.div`
  margin-bottom: 28px;
`;

const GroupTitle = styled.h3`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${brand_orange_deep};
  margin: 0 0 12px;
`;

const Stack = styled.ol`
  list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 12px;
`;

const Row = styled.li`
  display: grid;
  grid-template-columns: 56px 1fr auto;
  gap: 18px;
  align-items: center;
  padding: 18px 20px;
  border: 1px solid ${({ $next }) => ($next ? "rgba(216, 89, 7, 0.45)" : "#e5e7eb")};
  border-radius: 16px;
  background: ${({ $next }) =>
    $next
      ? "linear-gradient(135deg, rgba(216, 89, 7, 0.06), rgba(247, 179, 12, 0.05))"
      : "#ffffff"};
  transition: box-shadow 200ms ease, transform 200ms ease, border-color 200ms ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${card_shadow_md};
    border-color: rgba(216, 89, 7, 0.32);
  }

  @media ${narrowScreenMediaQuery} {
    grid-template-columns: 40px 1fr;
    grid-template-areas:
      "idx body"
      "cta cta";
    gap: 10px;
    padding: 14px 16px;

    & > .idx-area  { grid-area: idx; }
    & > .body-area { grid-area: body; }
    & > .cta-area  { grid-area: cta; }
  }

  [data-theme="dark"] & {
    background: ${({ $next }) =>
      $next ? "rgba(216, 89, 7, 0.10)" : "rgba(255,255,255,0.03)"};
    border-color: ${({ $next }) =>
      $next ? "rgba(216, 89, 7, 0.5)" : "rgba(255,255,255,0.08)"};
  }
`;

const IndexBubble = styled.div`
  width: 40px; height: 40px;
  border-radius: 50%;
  background: ${({ $next }) =>
    $next ? brand_gradient : "rgba(15, 23, 42, 0.06)"};
  color: ${({ $next }) => ($next ? "#fff" : "#4b5563")};
  display: flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 15px;
  box-shadow: ${({ $next }) =>
    $next ? "0 4px 12px rgba(216, 89, 7, 0.32)" : "none"};

  [data-theme="dark"] & {
    color: ${({ $next }) => ($next ? "#fff" : "#cbd5e1")};
    background: ${({ $next }) => ($next ? brand_gradient : "rgba(255, 255, 255, 0.06)")};
  }
`;

const Body = styled.div`
  display: flex; flex-direction: column; gap: 8px; min-width: 0;
`;

const Heading = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.4;
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  [data-theme="dark"] & { color: #f3f4f6; }
`;

const PaidPill = styled.span`
  display: inline-block; padding: 2px 8px; border-radius: 999px;
  background: rgba(216,89,7,0.12); color: ${brand_orange_deep};
  font-size: 10px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase;
`;

const NextBadge = styled.span`
  display: inline-block; padding: 3px 10px; border-radius: 999px;
  background: ${brand_gradient}; color: #fff;
  font-size: 10px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase;
`;

const MetaRow = styled.div`
  display: flex; align-items: center; flex-wrap: wrap;
  font-size: 12px; color: #6b7280;
  [data-theme="dark"] & { color: #9ca3af; }
`;

const Meta = styled.span`
  & + & {
    &::before {
      content: "·";
      margin: 0 8px;
      color: rgba(15, 23, 42, 0.3);
      font-weight: 700;
    }
  }
`;

const ProgressRow = styled.div`
  display: flex; align-items: center; gap: 12px;
`;

const ProgressBar = styled.div`
  flex: 1; height: 6px; border-radius: 999px; background: rgba(0,0,0,0.08); overflow: hidden;
  [data-theme="dark"] & { background: rgba(255,255,255,0.12); }
`;

const ProgressFill = styled.div`
  height: 100%; background: ${brand_gradient};
`;

const Status = styled.span`
  font-size: 11px; font-weight: 700;
  color: ${({ $tone }) => ($tone === "success" ? "#047857" : "#6b7280")};
  text-transform: uppercase; letter-spacing: 0.04em;
  white-space: nowrap;
  [data-theme="dark"] & { color: ${({ $tone }) => ($tone === "success" ? "#34d399" : "#9ca3af")}; }
`;

const Cta = styled.div`
  display: flex; align-items: center; justify-content: flex-end;
  @media ${narrowScreenMediaQuery} { justify-content: flex-start; }
`;

const CtaLink = styled(Link)`
  display: inline-block;
  padding: 10px 20px; border-radius: 999px;
  background: ${({ $emphasis }) => ($emphasis ? brand_orange_deep : "transparent")};
  color: ${({ $emphasis }) => ($emphasis ? "#fff" : brand_orange_deep)};
  border: 2px solid ${brand_orange_deep};
  font-weight: 700; font-size: 12px; text-decoration: none; letter-spacing: 0.04em; text-transform: uppercase;
  white-space: nowrap;
  transition: filter 150ms ease, background 150ms ease, color 150ms ease;
  &:hover {
    background: ${brand_orange_deep};
    color: #fff;
    filter: brightness(0.95);
  }
  &:focus-visible { outline: 2px solid #fdba74; outline-offset: 2px; }
`;
